import styled, { css, keyframes } from 'styled-components';

import { ACQ, ACQ_MOBILE, ANIM, GRADIENTS } from '../../../../theme/tokens';
import { hoverWash } from '../../../../theme/mixins';
import { Button, Text } from '../../../../components';

export const H2 = styled(Text)`
  display: block;
  font-size: 22px;
  font-weight: 700;
  color: ${ACQ.white};
  margin: 6px 0 10px;
`;

// --- Tab row ----------------------------------------------------------------
// The atoms of the tab strip. NONE of them is rendered as-is any more: all three page-level
// strips (both campaign browsers and the wallet's activity/invoices strip) render the
// `CampaignBrowser.styles` subclasses, so `TabScroll` and `TabButton` are base classes only
// and carry the geometry the three share. `TabCount` is still rendered directly, by all
// three; `TabDot` and `TabIco` only by the two campaign browsers.
//
// The padded in-panel strip that used to live here (`TabRow`, a 68px bar on its own dark
// surface) is gone with its last renderer: the wallet moved its tabs onto the page too, so
// there is no strip inside a panel left anywhere.
export const TabScroll = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  width: 100%;
  height: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  overscroll-behavior-x: contain;
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

export const TabButton = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 48px;
  padding: 0 ${(p) => (p.$active ? '14px' : '10px')};
  border-radius: 12px;
  background: ${(p) => (p.$active ? ACQ.tabActiveBg : 'transparent')};
  border: 0;
  font-size: 14px;
  font-weight: 400;
  color: ${ACQ.t50};
  white-space: nowrap;
  flex-shrink: 0;
  cursor: pointer;
`;

export const TabDot = styled.i<{ $color: string }>`
  width: 6px;
  height: 6px;
  border-radius: 3px;
  flex-shrink: 0;
  background: ${(p) => p.$color};
`;

export const TabIco = styled.span`
  color: ${ACQ.t400};
  flex-shrink: 0;
  display: flex;
  align-items: center;
`;

export const TabCount = styled.span<{ $bg: string; $color: string }>`
  height: 18px;
  min-width: 18px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 6px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 400;
  background: ${(p) => p.$bg};
  color: ${(p) => p.$color};
`;

// The search field, its inline clear, the filter chips and the "Category" label all
// live in `CampaignBrowser.styles` now: both browsers share one header row, so the
// toolbar strip, the collapsible filter tray and the "Clear all filters" link this
// file used to hold have no renderer left.

// --- Campaign cards grid ----------------------------------------------------
const cardIn = keyframes`
  from { opacity: 0; transform: translateY(10px) scale(0.985); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
`;
const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

// "Just activated" green hue: a one-shot wash + green ring + outer glow that
// holds, then fades to nothing. Driven by box-shadow (the card keeps its own
// background) — an inset shadow with a huge spread tints the whole card, while
// the outer ring/glow ride the rounded corners. Shared by the acquisition
// Campaigns and the operations Promotions cards via `activatedHueCss`.
const activatedHue = keyframes`
  0%   { box-shadow: inset 0 0 0 9999px ${ACQ.activatedFill}, 0 0 0 1.5px ${ACQ.success}, 0 0 26px 2px ${ACQ.activatedGlow}; }
  62%  { box-shadow: inset 0 0 0 9999px ${ACQ.activatedFill}, 0 0 0 1.5px ${ACQ.success}, 0 0 26px 2px ${ACQ.activatedGlow}; }
  100% { box-shadow: inset 0 0 0 9999px transparent, 0 0 0 0 transparent, 0 0 0 0 transparent; }
`;

/**
 * Apply the one-shot "just activated" green hue (self-fading over
 * `ANIM.ACTIVATED_HUE_MS`). Reduced-motion users skip the flash and rely on the
 * persistent "Just activated" tagline instead.
 */
export const activatedHueCss = css`
  animation: ${activatedHue} ${ANIM.ACTIVATED_HUE_MS}ms ease both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  padding: 0 6px 10px;

  ${ACQ_MOBILE} {
    grid-template-columns: 1fr;
  }
`;

export const Cell = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  animation: ${cardIn} 0.44s ${ACQ.ease} both;

  > div {
    flex: 1;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

// Empty / no-results state for a tab. A centred column — tinted icon badge,
// title, supporting line and an optional action — shared by the acquisition
// Campaigns grid (where `grid-column: 1 / -1` spans both columns) and the
// operations Promotions list (a flex column, where it's simply ignored).
export const Empty = styled.div`
  grid-column: 1 / -1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 10px;
  min-height: 200px;
  padding: 36px 20px;
  animation: ${fadeIn} 0.28s ease both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const EmptyIcon = styled.div`
  display: grid;
  place-items: center;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${ACQ.focusBg};
  border: 1px solid ${ACQ.focusBorder};
  color: ${ACQ.t400};
  margin-bottom: 2px;
`;

export const EmptyTitle = styled(Text)`
  font-size: 16px;
  font-weight: 700;
  color: ${ACQ.t100};
  line-height: 1.2;
`;

export const EmptyText = styled(Text)`
  max-width: 320px;
  font-size: 13px;
  font-weight: 400;
  color: ${ACQ.placeholder};
  line-height: 1.45;
`;

export const EmptyAction = styled.button`
  margin-top: 6px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 40px;
  padding: 0 18px;
  border-radius: 9999px;
  background: ${ACQ.orangeTint};
  border: 1px solid ${ACQ.orangeBorder};
  color: ${ACQ.orange};
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  /* Cursor-following hover glow + lit hairline ring (shared with the CTA Button). */
  ${hoverWash}
  transition: background-color ${ANIM.INTERACT_MS}ms ease;

  &:hover {
    background: ${ACQ.orangeManageHover};
  }
`;

export const EmptyActionText = styled(Text)`
  font-size: 13px;
  font-weight: 700;
  color: ${ACQ.orange};
`;

export const Chev = styled.span`
  display: grid;
  place-items: center;
  color: ${ACQ.t50};
  flex-shrink: 0;
  transition: transform 0.25s ${ACQ.ease};
`;

export const Card = styled.div<{ $justActivated?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 10px;
  background: ${ACQ.campCard};
  border-radius: 16px;
  padding: 20px;
  cursor: pointer;
  ${hoverWash}

  &:hover ${Chev} {
    transform: translateX(2px);
  }

  ${(p) => (p.$justActivated ? activatedHueCss : '')}
`;

export const Info = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const HeadRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`;

export const Period = styled(Text)`
  font-size: 12px;
  font-weight: 400;
  color: ${ACQ.orange};
  line-height: 1.15;
`;

export const Live = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 10px;
  font-weight: 400;
  line-height: 1;
  color: ${ACQ.live};
  white-space: nowrap;
`;

export const LiveDot = styled.i`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${ACQ.live};
  flex-shrink: 0;
`;

export const Main = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

export const Thumb = styled.img`
  width: 60px;
  height: 60px;
  border-radius: 16px;
  flex-shrink: 0;
  object-fit: cover;
`;

export const Title = styled(Text)`
  flex: 1;
  min-width: 0;
  font-size: 18px;
  font-weight: 700;
  color: ${ACQ.t300};
  line-height: 1.15;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
`;

// Wraps the title so the "Just activated" tagline can sit directly beneath it
// (taking the flex slot the title held in the Thumb · Title · Chevron row).
export const TitleBox = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

// Transient "Just activated" tagline under a freshly-activated card's title —
// stays for the life of the visit (cleared on navigate-away), unlike the green
// hue which self-fades. Shared with the operations Promotions card.
export const JustActivated = styled(Text)`
  font-size: 12px;
  font-weight: 600;
  line-height: 1.1;
  color: ${ACQ.success};
  animation: ${fadeIn} 0.3s ease both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const Divider = styled.div`
  height: 1px;
  background: ${GRADIENTS.dividerStyledH};
`;

export const Cta = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const ExpLabel = styled(Text)`
  font-weight: 300;
  font-size: 12px;
  color: ${ACQ.t400};
`;

export const Timer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-variant-numeric: tabular-nums;
`;

export const TUnit = styled.span<{ $color: string }>`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  color: ${(p) => p.$color};
`;

export const TimerNum = styled(Text)`
  font-weight: 700;
  font-size: 14px;
  letter-spacing: 0.28px;
`;

export const TimerUnit = styled(Text)`
  font-style: normal;
  font-weight: 400;
  font-size: 12px;
`;

export const TSep = styled(Text)<{ $color: string }>`
  font-weight: 700;
  font-size: 14px;
  color: ${(p) => p.$color};
  letter-spacing: 0.28px;
`;

export const Meta = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

export const MetaIco = styled.span`
  color: ${ACQ.t400};
  flex-shrink: 0;
  display: flex;
`;

export const MetaVal = styled(Text)`
  font-size: 12px;
  font-weight: 500;
  color: ${ACQ.t300};
  font-variant-numeric: tabular-nums;
`;

export const LoadMore = styled.button`
  grid-column: 1 / -1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 48px;
  background: transparent;
  border: 0;
  border-radius: 9999px;
  color: ${ACQ.white};
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
`;

export const LoadMoreText = styled(Text)`
  font-size: 14px;
  font-weight: 700;
  color: ${ACQ.white};
`;

// Sizing-only wrapper around the shared <Button variant="text">: the campaigns
// grid is two columns, so the "Load more" row must span both. (No colour/border
// overrides — theming stays with the shared Button.)
export const LoadMoreButton = styled(Button)`
  grid-column: 1 / -1;
`;
